import { useState, useEffect, useCallback, useMemo } from "react";
import { getSubOrganizations, searchSubOrg } from "../../../../../../api";
import useOrganizationStore from "../../../../../stores/organization";
import { debounce } from "lodash";

const useSubOrganizations = (organizationId = null) => {
  const subOrgs = useOrganizationStore((state) => state.subOrgs);
  const setSubOrgs = useOrganizationStore((state) => state.setSubOrgs);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const selectedOrganizationData = useOrganizationStore(
    (state) => state.selectedOrganizationData,
  );
  const selectedOrganizationId =
    organizationId || selectedOrganizationData?.organization?.id;

  const fetchSubOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSubOrganizations(selectedOrganizationId);
      if (response.status) {
        setSubOrgs(response.data.data);
      } else {
        console.error("Failed to fetch sub-organizations");
        setError("Failed to fetch sub-organizations");
      }
    } catch (error) {
      console.error(
        "An error occurred while fetching sub-organizations",
        error,
      );
      setError("An error occurred while fetching sub-organizations");
    } finally {
      setLoading(false);
    }
  }, [selectedOrganizationId, setSubOrgs]);

  const searchSubOrganizations = useCallback(async (searchTerm) => {
    try {
      const response = await searchSubOrg({ search_term: searchTerm });
      if (response.status) {
        setSubOrgs(response.data.data);
      } else {
        setError("Failed to fetch sub-organizations");
      }
    } catch (error) {
      setError("An error occurred while fetching sub-organizations");
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const debouncedSearchSubOrganizations = useMemo(() => {
    return debounce((term) => {
      searchSubOrganizations(term);
    }, 300);
  }, [searchSubOrganizations]);

  // debounce this
  const handleSearchTextChange = (e) => {
    setSearchTerm(e.target.value);
    setSearchLoading(true);
    if (e.target.value.length === 0) {
      fetchSubOrganizations();
    } else {
      debouncedSearchSubOrganizations(e.target.value);
    }
  };

  useEffect(() => {
    if (selectedOrganizationId) {
      fetchSubOrganizations();
    }

    return () => {
      debouncedSearchSubOrganizations.cancel();
    };
  }, [
    fetchSubOrganizations,
    selectedOrganizationId,
    debouncedSearchSubOrganizations,
  ]);

  return {
    subOrgs,
    searchLoading,
    loading,
    error,
    searchTerm,
    handleSearchTextChange,
    fetchSubOrganizations,
    debouncedSearchSubOrganizations,
  };
};

export default useSubOrganizations;
